import { SFNClient, StartExecutionCommand } from '@aws-sdk/client-sfn';
import { Icon } from '@iconify/react';
import { Box, Checkbox, Typography } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import awsConfig from '../../../amplify_outputs.json';
import SendButton from '../../../components/custom/SendButton';
import { useAuth } from '../../../utils/context/useAuthContext';
import { useUtils } from '../../../utils/context/useUtilsContext';
import { formatFileDate } from '../../../utils/format/format';

const apiClient = generateClient<Schema>();

const STEP_FUNCTION_ARN = awsConfig.custom.processFileStepFunctionArn;
const S3_BUCKET_NAME = awsConfig.storage.bucket_name;

const UploadFile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');

  const { isUploadFileDialogOpen, closeUploadDialog } = useUtils();

  const { user } = useAuth();
  const userId = user?.userId;

  const navigate = useNavigate();

  const uuid = clientId || userId;
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [isReplace, setIsReplace] = useState(false);
  const [replace, setReplace] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadFileRef = useRef<HTMLDivElement | null>(null);

  const handleBoxClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsReplace(false);
    setReplace(false);
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const startStepFunction = async (fileId: string, replace: boolean) => {
    try {
      const triggerProcessFile = await apiClient.queries.triggerProcessFile({
        fileId,
        replace,
        arn: STEP_FUNCTION_ARN,
      });

      console.log('Step Function started:', triggerProcessFile);

      closeUploadDialog();
    } catch (error) {
      console.error('Failed to start Step Function:', error);
    }
  };

  const resetComponent = () => {
    setFile(null);
    setIsReplace(false);
    setReplace(false);
    closeUploadDialog();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
    console.log('Component state has been reset.');
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get pure base64
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleNextStep = async () => {
    if (!file || !uuid) {
      console.error('No file selected or UUID missing');
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      const text = e.target?.result;
      if (typeof text !== 'string') {
        console.error('Invalid file content');
        return;
      }

      try {
        const executionDateMatch = text.match(
          /<TAARICH-BITZUA>(.*?)<\/TAARICH-BITZUA>/,
        );
        const rawExecutionDate = executionDateMatch
          ? executionDateMatch[1]
          : '';
        const fileExecutionDate = formatFileDate(rawExecutionDate);

        if (!fileExecutionDate) {
          console.error('Failed to extract execution date from file.');
          return;
        }

        console.log('Execution Date from file:', fileExecutionDate);

        const fileListResponse = await apiClient.models.File.listFileByDate(
          {
            date: fileExecutionDate,
          },
          {
            filter: { userId: { eq: uuid } },
          },
        );

        const existingFile = fileListResponse?.data?.[0];

        if (!existingFile) {
          const createdFile = await apiClient.models.File.create({
            date: fileExecutionDate,
            status: 'PROCESSING',
            userId: uuid,
            fileName: file.name,
            viewed: true
          });

          const fileId = createdFile?.data?.id;
          if (!fileId) {
            console.error('Failed to create file record.');
            return;
          }

          console.log('Uploading file to S3...');
          try {
            // Convert file to base64
            const base64File = await fileToBase64(file);
            
            const uploadResponse = await apiClient.queries.uploadFile({
              file: base64File,
              path: `files/${fileId}`,
              bucketName: S3_BUCKET_NAME,
            });

            console.log('File successfully uploaded to S3 with ID:', fileId);
            console.log('Upload response:', uploadResponse);

            await startStepFunction(fileId, false);
            navigate(`/file-overview?fileId=${fileId}`);
          } catch (uploadError) {
            console.error('Failed to upload file to S3:', uploadError);
          }
        } else {
          if (replace) {
            console.log('Replacing file in S3...');
            try {
              // Convert file to base64
              const base64File = await fileToBase64(file);
              
              const uploadResponse = await apiClient.queries.uploadFile({
                file: base64File,
                path: `files/${existingFile.id}`,
                bucketName: S3_BUCKET_NAME,
              });

              console.log(
                'File successfully replaced in S3 with ID:',
                existingFile.id,
              );
              console.log('Upload response:', uploadResponse);

              const updateFile = await apiClient.models.File.update({
                id: existingFile.id,
                status: 'PROCESSING',
              });

              await startStepFunction(existingFile.id, true);
              navigate(`/file-overview?fileId=${existingFile.id}`);
            } catch (uploadError) {
              console.error('Failed to replace file in S3:', uploadError);
            }
          } else {
            setIsReplace(true);
            console.log(
              'File already exists. Waiting for user confirmation to replace.',
            );
          }
        }
      } catch (error) {
        console.error('Error processing file:', error);
      }
    };

    reader.readAsText(file);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        uploadFileRef.current &&
        !uploadFileRef.current.contains(event.target as Node)
      ) {
        closeUploadDialog();
      }
    };

    if (isUploadFileDialogOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadFileDialogOpen]);

  return (
    <motion.div
      ref={uploadFileRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: isUploadFileDialogOpen ? 1 : 0 }}
      transition={{ duration: 0.2 }}
      style={{
        position: 'fixed',
        top: 0,
        right: 140,
        zIndex: 1000,
        width: '780px',
        height: '100vh',
        backgroundColor: '#343434F7',
        display: 'flex',
        flexDirection: 'column',
        padding: '40px',
        boxSizing: 'border-box',
        pointerEvents: isUploadFileDialogOpen ? 'auto' : 'none',
        gap: '20px',
      }}
    >
      <Box
        sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
      >
        <Box sx={{ cursor: 'pointer' }} onClick={closeUploadDialog}>
          <Icon icon="ph:arrow-left-bold" color="#FFF" width={32} height={32} />
        </Box>
        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <Typography
            sx={{ color: '#FFF' }}
            fontWeight={'900'}
            fontSize={'14px'}
            dir="rtl"
          >
            יש להעלות קובץ XML בלבד
          </Typography>
          <img src="/assets/uploadFile/xml.svg" alt="Upload Icon" draggable="false" />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '318px',
          backgroundColor: '#0000008A',
          borderRadius: '15px',
          cursor: 'pointer',
        }}
        onClick={handleBoxClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <img src="/assets/uploadFile/coins.svg" alt="Upload"  draggable="false" />
          <Typography
            sx={{ color: '#FFF' }}
            fontSize={'12px'}
            fontWeight={'700'}
          >
            לחץ כאן או גרור את הקובץ
          </Typography>
        </Box>
        <input
          type="file"
          accept=".dat"
          ref={inputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
      {file && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: '10px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#00000042',
              py: '5px',
              px: '15px',
              display: 'flex',
              gap: '5px',
              alignItems: 'center',
              borderRadius: '30px',
              width: '100%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
              color={'#FFF'}
            >
              {file.name}
            </Typography>
            <Icon
              style={{ cursor: 'pointer' }}
              icon={'material-symbols:close'}
              color="#FFF"
              onClick={() => {
                setFile(null);
                setIsReplace(false);
              }}
            />
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {file && (
          <SendButton
            loading={loading}
            label="העלאת קובץ"
            onClick={handleNextStep}
          />
        )}
        {isReplace && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
                color={'#FFF'}
                textAlign={'right'}
              >
                הקובץ כבר קיים
              </Typography>
              <Typography
                fontWeight={'700'}
                fontSize={'14px'}
                fontFamily={'AlmoniDL'}
                color={'#FFF'}
                dir={'rtl'}
                textAlign={'right'}
              >
                האם להחליף אותו?
              </Typography>
            </Box>
            <Checkbox
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
              sx={{
                color: '#FFFFFF',
                '&.Mui-checked': {
                  color: '#FFFFFF',
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                transition: 'none',
                '& .MuiSvgIcon-root': {
                  transition: 'none',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default UploadFile;
